import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  USE_PHASE_TEMPLATE,
  USE_PHASE_STATUS_LABELS,
  type UsePhaseStatus,
} from "@/lib/basProcess/constants";
import { ClipboardCheck, Plus, Trash2, Download, BellRing } from "lucide-react";
import { usePhaseReviewState, formatDate } from "@/lib/basProcess/reminders";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateUsePhaseFollowupWordDocument } from "@/lib/documentGenerator/bruksuppfoljningWordGenerator";

interface Props {
  projectId: string;
}

interface Followup {
  id: string;
  space_name: string;
  description: string | null;
  status: string;
  created_at: string;
  review_interval_months: number;
  last_reviewed_at: string | null;
  last_reviewed_by_name: string | null;
}

interface Item {
  id: string;
  followup_id: string;
  section: string;
  item_key: string;
  control_point: string;
  legal_reference: string | null;
  explanation: string | null;
  status: string;
  responsible: string | null;
  checked_at: string | null;
  comment: string | null;
  sort_order: number;
}

export const UsePhaseFollowup: React.FC<Props> = ({ projectId }) => {
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newSpace, setNewSpace] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    const { data, error } = await supabase
      .from("use_phase_followups")
      .select(
        "id, space_name, description, status, created_at, review_interval_months, last_reviewed_at, last_reviewed_by_name"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte läsa uppföljningen");
    const list = (data || []) as Followup[];
    setFollowups(list);
    if (list.length && !activeId) setActiveId(list[0].id);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const loadItems = async () => {
      if (!activeId) return setItems([]);
      const { data, error } = await supabase
        .from("use_phase_followup_items")
        .select("*")
        .eq("followup_id", activeId)
        .order("sort_order", { ascending: true });
      if (error) toast.error("Kunde inte läsa kontrollpunkter");
      else setItems((data || []) as Item[]);
    };
    loadItems();
  }, [activeId]);

  const createFollowup = async () => {
    if (!newSpace.trim()) return;
    const { data, error } = await supabase
      .from("use_phase_followups")
      .insert({ project_id: projectId, space_name: newSpace.trim() })
      .select("id")
      .single();
    if (error || !data) return toast.error("Kunde inte skapa uppföljningen");

    let order = 0;
    const rows = USE_PHASE_TEMPLATE.flatMap((sec) =>
      sec.items.map((it) => ({
        followup_id: data.id,
        section: sec.section,
        item_key: it.key,
        control_point: it.controlPoint,
        legal_reference: it.legalReference ?? null,
        explanation: it.explanation ?? null,
        sort_order: order++,
      }))
    );
    const { error: itemErr } = await supabase.from("use_phase_followup_items").insert(rows);
    if (itemErr) toast.error("Kunde inte skapa kontrollpunkter");

    setNewSpace("");
    setActiveId(data.id);
    await loadAll();
    toast.success("Uppföljning skapad");
  };

  const removeFollowup = async (id: string) => {
    await supabase.from("use_phase_followups").delete().eq("id", id);
    setActiveId(null);
    await loadAll();
  };

  const updateItem = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    const { error } = await supabase.from("use_phase_followup_items").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const setStatus = (item: Item, status: UsePhaseStatus) =>
    updateItem(item.id, {
      status,
      checked_at:
        status === "unanswered" ? null : item.checked_at ?? new Date().toISOString().slice(0, 10),
    });

  const exportWord = async () => {
    const active = followups.find((f) => f.id === activeId);
    if (!active) return;
    try {
      await generateUsePhaseFollowupWordDocument({
        space_name: active.space_name,
        description: active.description,
        status: active.status,
        items: items.map((i) => ({
          section: i.section,
          control_point: i.control_point,
          legal_reference: i.legal_reference,
          status: i.status,
          responsible: i.responsible,
          checked_at: i.checked_at,
          comment: i.comment,
        })),
      });
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const dueList = followups
    .map((f) => ({
      followup: f,
      review: usePhaseReviewState(f.last_reviewed_at, f.review_interval_months, f.created_at),
    }))
    .filter((r) => r.review.overdue);

  const setInterval = async (id: string, months: number) => {
    setFollowups((prev) =>
      prev.map((f) => (f.id === id ? { ...f, review_interval_months: months } : f))
    );
    const { error } = await supabase
      .from("use_phase_followups")
      .update({ review_interval_months: months })
      .eq("id", id);
    if (error) toast.error("Kunde inte spara intervallet");
  };

  const confirmReview = async (id: string) => {
    const { data: auth } = await supabase.auth.getUser();
    const name =
      (auth?.user?.user_metadata?.full_name as string | undefined) ||
      auth?.user?.email ||
      "okänd";
    const at = new Date().toISOString();
    const { error } = await supabase
      .from("use_phase_followups")
      .update({ last_reviewed_at: at, last_reviewed_by_name: name })
      .eq("id", id);
    if (error) return toast.error("Kunde inte kvittera uppföljningen");
    setFollowups((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, last_reviewed_at: at, last_reviewed_by_name: name } : f
      )
    );
    toast.success("Uppföljningen är kvitterad");
  };

  const activeFollowup = followups.find((f) => f.id === activeId) || null;
  const activeReview = activeFollowup
    ? usePhaseReviewState(
        activeFollowup.last_reviewed_at,
        activeFollowup.review_interval_months,
        activeFollowup.created_at
      )
    : null;

  const checked = items.filter((i) => i.status !== "unanswered").length;
  const progress = items.length ? Math.round((checked / items.length) * 100) : 0;
  const sections = Array.from(new Set(items.map((i) => i.section)));

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar uppföljningen...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="33"
        exportHistory={{ documentType: "uppfoljning_bruksskede" }}
        title="Uppföljning bruksskede – arbetsplatsens utformning"
        description="Slutlig verifiering av att funktionskraven i Dok 02 är uppfyllda i det färdiga byggnadsverket. Lämnas över som del av arbetsmiljödokumentationen."
        icon={ClipboardCheck}
        actions={
          <div className="flex gap-2">
            <Input
              className="w-56"
              placeholder="Utrymme, t.ex. Fläktrum plan 5"
              value={newSpace}
              onChange={(e) => setNewSpace(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createFollowup()}
            />
            <Button onClick={createFollowup}><Plus className="h-4 w-4 mr-1" />Lägg till</Button>
            {activeId && (
              <Button variant="outline" onClick={exportWord}>
                <Download className="h-4 w-4 mr-1" />Word
              </Button>
            )}
          </div>
        }
      />

      {dueList.length > 0 && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <BellRing className="h-4 w-4 text-destructive" />
              {dueList.length} uppföljningar ska göras om
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dueList.map(({ followup, review }) => (
              <div
                key={followup.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background p-2"
              >
                <span className="text-sm">
                  <span className="font-medium">{followup.space_name}</span> · skulle vara gjord{" "}
                  {formatDate(review.dueAt)} ({Math.abs(review.daysLeft ?? 0)} dagar sedan)
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setActiveId(followup.id)}>
                    Öppna
                  </Button>
                  <Button size="sm" onClick={() => confirmReview(followup.id)}>
                    Kvittera
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {activeFollowup && activeReview && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                Återkommande uppföljning – {activeFollowup.space_name}
              </p>
              <p className="text-xs text-muted-foreground">
                {activeFollowup.last_reviewed_at
                  ? `Senast kvitterad ${formatDate(activeFollowup.last_reviewed_at)}${activeFollowup.last_reviewed_by_name ? ` av ${activeFollowup.last_reviewed_by_name}` : ""}.`
                  : "Aldrig kvitterad."}{" "}
                Nästa gång senast {formatDate(activeReview.dueAt)}.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={activeFollowup.review_interval_months}
                onChange={(e) => setInterval(activeFollowup.id, Number(e.target.value))}
              >
                <option value={3}>Var 3:e månad</option>
                <option value={6}>Var 6:e månad</option>
                <option value={12}>Årligen</option>
                <option value={24}>Vartannat år</option>
              </select>
              <Button size="sm" onClick={() => confirmReview(activeFollowup.id)}>
                Kvittera uppföljning
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {followups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <ClipboardCheck className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Ingen uppföljning påbörjad. Lägg till ett utrymme för att kontrollera funktionskraven på plats.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {followups.map((f) => (
              <Button
                key={f.id}
                size="sm"
                variant={activeId === f.id ? "default" : "outline"}
                onClick={() => setActiveId(f.id)}
              >
                {f.space_name}
              </Button>
            ))}
          </div>

          {activeId && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-base">
                    {followups.find((f) => f.id === activeId)?.space_name}
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="w-40">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Kontrollerat</span><span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeFollowup(activeId)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {sections.map((section) => (
                  <div key={section} className="space-y-3">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {section}
                    </h4>
                    <ul className="divide-y">
                      {items.filter((i) => i.section === section).map((item) => (
                        <li key={item.id} className="py-3 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{item.control_point}</span>
                            {item.legal_reference && (
                              <Badge variant="secondary" className="text-[10px]">{item.legal_reference}</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(Object.keys(USE_PHASE_STATUS_LABELS) as UsePhaseStatus[]).map((s) => (
                              <Button
                                key={s}
                                size="sm"
                                variant={
                                  item.status === s ? (s === "deviation" ? "destructive" : "default") : "outline"
                                }
                                onClick={() => setStatus(item, s)}
                              >
                                {USE_PHASE_STATUS_LABELS[s]}
                              </Button>
                            ))}
                          </div>
                          <div className="grid gap-2 sm:grid-cols-4">
                            <Input
                              placeholder="Ansvarig"
                              defaultValue={item.responsible ?? ""}
                              onBlur={(e) => updateItem(item.id, { responsible: e.target.value })}
                            />
                            <Input
                              type="date"
                              defaultValue={item.checked_at ?? ""}
                              onBlur={(e) => updateItem(item.id, { checked_at: e.target.value || null })}
                            />
                            <Textarea
                              className="sm:col-span-2"
                              rows={1}
                              placeholder="Kommentar / åtgärd"
                              defaultValue={item.comment ?? ""}
                              onBlur={(e) => updateItem(item.id, { comment: e.target.value })}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default UsePhaseFollowup;
